import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { environment } from '@envs/environment'
import { plainToInstance } from 'class-transformer'
import { map, Observable } from 'rxjs'
import { ChangeRequestCreatePayload, ChangeItemRequest } from '../model'
import { ReportSubscription } from '@shared/services/websocket.service'
import { ExchangeRate } from '@app/features/scope-overview/model/exchange-rate.model'

@Injectable({ providedIn: 'root' })
export class ChangeRequestService {
  private readonly BASE_API_URL: string = environment.scopeApiUrl

  constructor(private http: HttpClient) {}

  createChangeRequest(scopeId: number, payload: ChangeRequestCreatePayload) {
    return this.http.post(`${this.BASE_API_URL}scopes/${scopeId}/change-requests`, payload)
  }

  getChangeRequests(scopeId: number): Observable<ChangeRequest[]> {
    return this.http
      .get<any[]>(`${this.BASE_API_URL}scopes/${scopeId}/change-requests`)
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest[]))
  }

  getChangeRequest(scopeId: number, changeRequestId: number): Observable<ChangeRequest> {
    return this.http
      .get<any>(`${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}`)
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }

  changeRequestAbleToMerge(scopeId: number, changeRequestId: number): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/able-to-merge`
    )
  }

  submitChangeRequest(scopeId: number, changeRequestId: number): Observable<ChangeRequest> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/approve`
    return this.http
      .post<any>(url, {})
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }

  dismissChangeRequest(
    scopeId: number,
    changeRequestId: number,
    options?: { comment?: string }
  ): Observable<ChangeRequest> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/close`
    const body = options?.comment?.trim() ? { comment: options.comment.trim() } : {}
    return this.http
      .post<any>(url, body)
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }

  mergeChangeRequest(
    scopeId: number,
    changeRequestId: number,
    options?: { comment?: string }
  ): Observable<ChangeRequest> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/merge`
    const body = options?.comment?.trim() ? { comment: options.comment.trim() } : {}
    return this.http
      .post<any>(url, body)
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }

  revertChangeRequestToDraft(
    scopeId: number,
    changeRequestId: number,
    options?: { comment?: string }
  ): Observable<ChangeRequest> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/draft`
    const body = options?.comment?.trim() ? { comment: options.comment.trim() } : {}
    return this.http
      .post<any>(url, body)
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }

  trafficChangeRequest(
    scopeIdentityId: number,
    changeRequestId: number,
    integrationNames: string[],
    exchangeRateDetails: ExchangeRate
  ): Observable<ReportSubscription> {
    const url = `${this.BASE_API_URL}change-requests/traffic`
    const params = {
      changeRequestId,
      scopeIdentityId,
      integrationNames,
      exchangeRateDetails,
    }
    return this.http.post<ReportSubscription>(url, params)
  }

  applyChanges(scopeId: number, changeRequestId: number, changeItemRequest: ChangeItemRequest): Observable<any> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/changes`
    return this.http.post<any>(url, changeItemRequest)
  }

  modifyChange(
    scopeId: number,
    changeRequestId: number,
    changeItemId: string,
    changeItemRequest: ChangeItemRequest
  ): Observable<any> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/changes/${changeItemId}`
    return this.http.put<any>(url, changeItemRequest)
  }

  deleteChangeItem(scopeId: number, changeRequestId: number, changeItemUuid: string): Observable<void> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/changes/${changeItemUuid}`
    return this.http.delete<void>(url)
  }

  getChangeRequestPreview(scopeId: number, changeRequestId: number): Observable<ScopeVersion> {
    return this.http
      .get<any>(`${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/preview`)
      .pipe(map((response) => plainToInstance(ScopeVersion, response as ScopeVersion)))
  }

  generateChangesFromScenarioConfiguration(scopeId: number, changeRequestId: number): Observable<ChangeRequest> {
    const url = `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/scenario-configuration-changes`
    return this.http
      .post<any>(url, {})
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }

  getNetChanges(scopeId: number, changeRequestId: number): Observable<ScopeVersion> {
    return this.http
      .get<any>(`${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/net-changes`)
      .pipe(map((response) => plainToInstance(ScopeVersion, response) as ScopeVersion))
  }

  rebaseChanges(scopeId: number, changeRequestId: number): Observable<ChangeRequest> {
    return this.http
      .post<any>(
        `${this.BASE_API_URL}scopes/${scopeId}/change-requests/${changeRequestId}/scenario-configuration-rebase`,
        {}
      )
      .pipe(map((response) => plainToInstance(ChangeRequest, response) as ChangeRequest))
  }
}
